import React, { useEffect, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import TagManager from 'react-gtm-module';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import './StripePayment.css';
import { kirkos_logo_white_yellow } from "../../assets/images";
import { useMutation } from '@tanstack/react-query';
import axiosInstance from '../../services/axios.instance';
import { API_URL } from '../../services/apiConfig';
import { useDispatch } from 'react-redux';
import { clearBookingValues } from '../../Redux/BookingData/bookingDataSlice';
import { trackEvent } from '../../services/events';
import { toast } from 'react-toastify';
import DraggableWhatsAppIcon from '../CustomerDashBoard/DraggableWhatsAppIcon';

// Stripe loading with retry logic
let stripePromise: Promise<any> | null = null;

const loadStripeWithRetry = async (maxRetries = 3, retryDelay = 1000) => {
  if (stripePromise) return stripePromise;

  stripePromise = (async () => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Loading Stripe.js (attempt ${attempt}/${maxRetries})...`);
        const stripe = await loadStripe(`${process.env.REACT_APP_STRIPE_URL}`);

        if (stripe) {
          console.log('Stripe.js loaded successfully');
          return stripe;
        }

        throw new Error('Stripe object is null');
      } catch (error) {
        console.warn(`Stripe.js load attempt ${attempt} failed:`, error);

        if (attempt === maxRetries) {
          console.error('Failed to load Stripe.js after all retries');
          throw new Error('Failed to load Stripe.js. Please check your internet connection and try again.');
        }

        // Exponential backoff: wait longer between each retry
        const waitTime = retryDelay * Math.pow(2, attempt - 1);
        console.log(`Retrying in ${waitTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  })();

  return stripePromise;
};

const StripePayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [elements, setElements] = useState<any>(null);
  const [paymentElement, setPaymentElement] = useState<any>(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);

  const { orderNumber, amount, clientSecret, customerId, merchantId, paymentMerchantInfo } = location.state;

  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);

  const handleCancel = () => {
    const returnPath = location.state?.returnPath || "/customer/processpayment";
    navigate(returnPath, {
      state: location.state
    });
  };


  const handleClear = useMutation({
    mutationFn: () =>
      axiosInstance.get(
        `${API_URL.CLEAR_CART}/${customerId}/carts/${merchantId}/clear`
      ),

    onSuccess: (successResponse) => {
      console.log("successResponse", JSON.stringify(successResponse));
      console.log("Booking values being cleared:");
      dispatch(clearBookingValues());
    },
    onError: (error: any) => {
      if (error?.data?.message) {
        console.error("Error clearing cart:", error);
      }
      console.log("Booking values being cleared:");
      dispatch(clearBookingValues());
    },
  });

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        setIsStripeLoading(true);
        setStripeError(null);

        const stripe = await loadStripeWithRetry();

        if (!stripe) {
          throw new Error("Stripe failed to initialize");
        }

        const paymentIntent = await stripe.retrievePaymentIntent(clientSecret);
        if (paymentIntent.paymentIntent) {
          setPaymentAmount(paymentIntent.paymentIntent.amount / 100);
        }

        const elements = stripe.elements({
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0570de',
              colorBackground: '#ffffff',
              colorText: '#30313d',
              colorDanger: '#df1b41',
              fontFamily: 'Ideal Sans, system-ui, sans-serif',
              spacingUnit: '2px',
              borderRadius: '4px',
            },
          },
        });

        const paymentElement = elements.create('payment', {
          layout: {
            type: 'tabs',
            defaultCollapsed: false,
          }
        });

        setElements(elements);
        setPaymentElement(paymentElement);
        setIsStripeLoading(false);
      } catch (error: any) {
        console.error('Stripe initialization error:', error);
        setStripeError(error.message || 'Failed to load payment system');
        setIsStripeLoading(false);
      }
    };

    initializeStripe();
  }, [clientSecret]);

  // Mount payment element after DOM is ready
  useEffect(() => {
    if (paymentElement && !isStripeLoading && !stripeError) {
      // Use setTimeout to ensure DOM is updated
      const timer = setTimeout(() => {
        const element = document.getElementById('payment-element');
        if (element) {
          paymentElement.mount('#payment-element');
        } else {
          console.error('Payment element div not found in DOM');
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [paymentElement, isStripeLoading, stripeError]);

  useEffect(() => {
    const trackPageLoad = async () => {
      await trackEvent({
        event_name: { web: "Kirkos_CompletePaymentScreen", app: "Kirkos_CompletePaymentScreen_App" },
        event_category: "Payment_Info",
        module: "Payment",
        payload: {},
      });
    };
    trackPageLoad();
  }, []);

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPaymentLoading(true);
    handleClear.mutate();
    const stripe = await stripePromise;
    if (!stripe || !elements) {
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/customer/paymentsuccess?order_number=${orderNumber}`,
      },
    });

    if (error) {
      await trackEvent({
        event_name: { web: "Kirkos_CompletePaymentScreen_PayNowButtonFailed", app: "Kirkos_CompletePaymentScreen_PayNowButtonFailed_App" },
        event_category: "Payment_Info",
        module: "Payment",
        payload: {
          status: "failed",
          error_message: error.message || "Payment failed",
          order_number: orderNumber,
        },
      });
      console.error("Payment confirmation error:", error);

      toast.error(error.message || "Payment failed. Please try again.");
    } else {
      await trackEvent({
        event_name: { web: "Kirkos_CompletePaymentScreen_PayNowButtonSuccess", app: "Kirkos_CompletePaymentScreen_PayNowButtonSuccess_App" },
        event_category: "Payment_Info",
        module: "Payment",
        payload: {
          status: "success",
          order_number: orderNumber,
          amount: amount,
        },
      });
      if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
      }
      ReactPixel.track('Purchase', {
        content_type: 'rental',
        content_name: 'Car Rental Payment',
        currency: 'AED',
        value: amount,
        order_number: orderNumber
      });
      TagManager.dataLayer({
        dataLayer: {
          event: 'booking_complete',
          page_type: 'standard',
          page_path: window.location.pathname,
          page_url: window.location.href,
          order_id: orderNumber,
          value: amount,
          currency: 'AED'
        }
      });
    }
    setIsPaymentLoading(false);
  };

  return (
    <div className="stripe-payment-container">
      <img
        src={kirkos_logo_white_yellow}
        alt="Kirkos Logo"
        className="kirkos-logo"
      />
      <div className="payment-details">

        <h2>Complete Your Payment</h2>

        {/* Show loading state */}
        {isStripeLoading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading payment system...</span>
            </div>
            <p className="mt-3">Loading secure payment system...</p>
          </div>
        )}

        {/* Show error state */}
        {stripeError && (
          <div className="alert alert-danger" role="alert">
            <h5>Payment System Error</h5>
            <p>{stripeError}</p>
            <button
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Show payment form when loaded */}
        {!isStripeLoading && !stripeError && (
          <>
            <div className="order-info">
              <p>
                Order Number: <span>{orderNumber}</span>
              </p>
              {/* <p>
                Payment Charge: <span>3.9 AED</span>
              </p> */}
              <p>
                Amount to Pay: <span>{paymentAmount} AED</span>
              </p>
              {/* {paymentMerchantInfo && (
                <p>
                  Amount to Pay to the Vehicle Provider at Recollection: <span>{paymentMerchantInfo.toFixed(2)}</span>
                </p>
              )} */}
            </div>
            <form onSubmit={handlePaymentSubmit}>
              <div id="payment-element" className="mb-4">
                {/* Stripe Elements will mount here */}
              </div>
              <div className="d-flex gap-3 align-items-center">
                <button
                  className="cancel-payment-button m-0"
                  type="button"
                  onClick={handleCancel}
                  style={{ flex: 1 }}
                >
                  CANCEL
                </button>
                <button
                  className="payment-button m-0"
                  disabled={isPaymentLoading || !elements}
                  type="submit"
                  style={{ flex: 1 }}
                >
                  {isPaymentLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    "Pay Now"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
      <DraggableWhatsAppIcon />
    </div>
  );
};

export default StripePayment;